import AddCardPage from "@/components/add-card/AddCardPage";
import { cardParserRegistry } from "@/components/shared/form-parsers/cardParsers";
import { ValidationError, formatValidationErrors } from "@/components/shared/form-parsers/validation";
import { SitesRepository } from "@/repositories/SitesRepository";
import type { Card } from "@stackje/common/config";
import { createFileRoute, getRouteApi, useNavigate, useSearch } from "@tanstack/react-router";
import { useInvalidateSiteCache } from "@/hooks/useSiteQuery";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { z } from 'zod';

const searchSchema = z.object({
  insertAfterIndex: z.number().optional()
});

export const Route = createFileRoute("/_protected/_site/add-card/$cardType")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { cardType } = Route.useParams();
  const search = useSearch({ from: '/_protected/_site/add-card/$cardType' });
  const routeApi = getRouteApi("/_protected/_site");
  const data = routeApi.useLoaderData();
  const invalidateSiteCache = useInvalidateSiteCache();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (formData: FormData) => {
    const cardType = formData.get("type");
    if (!cardType || typeof cardType !== "string") {
      return;
    }

    const parser = cardParserRegistry[cardType];
    if (!parser) {
      throw new Error("Invalid or unsupported card type");
    }

    let cardData: Card;
    try {
      cardData = parser(formData);
    } catch (e) {
      if (e instanceof ValidationError) {
        const errorMessages = formatValidationErrors(e.errors);
        throw new Error("Validation failed:\n" + errorMessages.join("\n"));
      }
      throw new Error("Unable to parse card data: " + e);
    }

    try {
      if (!data.config) {
        data.config = {
          title: data.siteDetails.title,
          description: data.siteDetails.description,
          fontSetId: data.siteDetails.fontSetId,
          pages: [{ path: "/", cards: [] }],
        };
      }

      const pages = data.config.pages ?? [];
      if (pages.length === 0) {
        pages.push({ path: "/", cards: [] });
      }

      const insertAfterIndex = search.insertAfterIndex;
      if (typeof insertAfterIndex === 'number' && insertAfterIndex >= 0 && insertAfterIndex < pages[0].cards.length) {
        pages[0].cards.splice(insertAfterIndex + 1, 0, cardData);
      } else {
        pages[0].cards = pages[0].cards.concat(cardData);
      }

      const { error } = await SitesRepository.update(data.id, data.userId, {
        config: {
          ...data.config,
          pages,
        },
      });
      if (error) {
        throw error;
      }

      invalidateSiteCache(data.userId);
      toast({ description: "Card saved." });
      navigate({ to: "/" });
    } catch (e) {
      throw new Error("Unable to add card: " + e);
    }
  };

  return (
    <AddCardPage
      type={cardType}
      onSubmit={handleSubmit}
    />
  );
}
